import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ACCENT_COLORS, BORDER_COLORS } from '../../utils/colors';

/**
 * Табличка с параметрами сервера — правый край ПЕРВОГО ЭКРАНА, по центру его высоты.
 *
 * Живёт внутри геро-блока (`HeroSection`) и уезжает вместе с ним. Плавающей (`position: fixed`,
 * как у `SocialRail`) она уже была — и это оказалось ошибкой: табличка ехала за прокруткой через
 * всю страницу. Заказчик: «кручу вниз, а эта херня справа едет со мной». Понадобится вернуть
 * приклеенный вариант — это осознанное решение, а не мелкая правка `position`.
 *
 * **Рейты и формат те же, что в геро-блоке лендинга** (`landing/index.html`, блок `stats`).
 * Разъезжаться двум витринам одного сервера нельзя: меняешь рейты тут — поменяй и там. Подписи
 * переводятся, значения нет: «×4», «RvR», «Remastered» одинаковы на всех языках.
 *
 * ТОЛЬКО ОТ `lg` (1200px). Ниже места у правого края нет: на планшетной ширине табличка лезет
 * на героев и на карточку запуска сервера в правом нижнем углу.
 */

/**
 * Статус сервера. ЗАГЛУШКА, переключается руками — одна строка.
 *
 * У бэкенда такого эндпоинта нет: в API есть только `/l2/ratings/online`, а это рейтинг игроков
 * по времени в игре, а не состояние мира. Заказчик 08.09.2026: «пока что просто поставь
 * мануально». Появится эндпоинт — запрос придёт сюда, разметка ниже не изменится.
 */
const SERVER_ONLINE = true;

/** Цвет статуса: зелёный — сервер поднят, серый — нет. Красный тут занят смыслом «удалить». */
const STATUS_COLOR = SERVER_ONLINE ? '#22c55e' : '#9ca3af';

/**
 * Строки таблички. Порядок = порядок показа, `labelKey` — ключ подписи в `home.json`.
 *
 * Было пять строк (рейты, формат, рас, городов, профессий) — 08.09.2026 заказчик оставил три:
 * рас/городов/профессий не то, что спрашивают про сервер в первую секунду, а высота таблички
 * здесь дорогая (см. пороги ниже). Освободившееся место ушло в кегль, а не в новые строки:
 * заказчик сказал, что табличка «маленькая невзрачная».
 *
 * Даты старта тут намеренно НЕТ, хотя на лендинге она первой строкой: на сайте её показывает
 * карточка `LaunchWidget` в правом нижнем углу, и два срока на одном экране — это два места,
 * которые придётся править синхронно.
 */
const STATS: {
  id: string;
  labelKey: string;
  value: string;
  accent?: boolean;
}[] = [
  // Рейты выделены цветом — это первое, что спрашивают про сервер. На лендинге ту же роль
  // играет дата старта, поэтому там подсвечена она.
  { id: 'rates', labelKey: 'stats.rates', value: '×4', accent: true },
  { id: 'format', labelKey: 'stats.format', value: 'RvR' },
  { id: 'chronicles', labelKey: 'stats.chronicles', value: 'Remastered' },
];

/**
 * Подпись и значение вынесены в константы, потому что у статуса СВОЁ значение (точка + слово),
 * а выглядеть он обязан ровно как остальные строки. Первая версия статуса была узкой полосой
 * по низу — сэкономила 30px высоты и получила от заказчика «первые три в одном стиле по
 * размеру и тут какой-то пук внизу». Экономия высоты не стоит выпадающей из ряда строки:
 * платим за неё порогами видимости (ниже), а не внешним видом.
 */
const LABEL_SX = {
  color: 'rgba(255,255,255,0.5)',
  fontSize: '0.55rem',
  fontWeight: 700,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  lineHeight: 1,
  mb: 0.65,
} as const;

const VALUE_SX = {
  fontSize: '1.25rem',
  fontWeight: 800,
  lineHeight: 1,
  letterSpacing: '-0.02em',
  fontVariantNumeric: 'tabular-nums',
} as const;

const ROW_SX = { py: 1.35, textAlign: 'center' } as const;

const ServerStatsRail: React.FC = () => {
  const { t } = useTranslation('home');

  return (
    <Box
      sx={{
        // ВНУТРИ первого экрана, а не приклеена к окну. Плавающей (`fixed`) она ехала за
        // прокруткой вместе с курсором — заказчик сказал прямо: «кручу вниз, а эта херня
        // справа едет со мной». Теперь табличка живёт в геро-блоке и уезжает вместе с ним,
        // как любой другой его элемент.
        position: 'absolute',
        right: 24,
        // По центру первого экрана — решение заказчика, принятое с оговоркой. Карточка старта
        // карточка приклеена к низу ОКНА, а первый экран считается от ШИРИНЫ, поэтому на низких окнах
        // они сходятся: на 1366×640 по центру было перекрытие 165px. Ниже стоит порог по высоте,
        // который такие окна отсекает. Совсем убрать конфликт можно только одним способом —
        // увести карточку от правого края.
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 90,
        display: { xs: 'none', lg: 'block' },
        // Пороги, за которыми табличка прячется. Причина одна: карточка запуска сервера приклеена
        // к низу ОКНА, а первый экран считается от ШИРИНЫ (высота контейнера — 57.552vw),
        // поэтому чем шире экран, тем ниже уезжает центр и тем выше должно быть окно.
        //
        // Пороги посчитаны, а не подобраны. Три величины, все замерены на живой странице:
        //
        //   низ таблички = min(465 + 0.2875 × (ширина − 1200), 672)
        //   верх карточки = высота окна − 238 (компактная карточка) или − 262 (обычная)
        //   запас, который держим = 6px
        //
        // Низ таблички ПЕРЕСТАЁТ расти после 1920px: у геро-блока `maxWidth: 1920`, дальше он
        // не тянется и центр стоит на месте. Поэтому лесенка конечная — ультраширокие мониторы
        // отдельных полос не требуют, худший случай для всех них один и тот же.
        //
        // Карточка на окнах ниже 860px переключается в компактный вид и стоит на 24px ниже,
        // так что при переходе через 860 зазор скачком УХУДШАЕТСЯ — пороги считаны по обычному
        // виду, худшему из двух.
        //
        // Полос четыре, а не три: полоса `1400–1599` и полоса `1600–1699` разведены ради
        // ходовых ноутбучных разрешений. Считать порог по худшей ширине полосы — значит
        // прятать табличку и на тех окнах внутри неё, где она помещается: одна полоса
        // `1400–1699` убивала 1536×864, хотя там 40px запаса.
        //
        // Проверено на живой странице по нижней границе каждой полосы (худший разрешённый
        // случай): 1399×766 (+6), 1599×848 (+30), 1699×877 (+6), 1700×940 (+6), 3440×940 (+6).
        // Ходовые разрешения: 1366×768 (+17), 1536×864 (+40), 1600×900 (+58), 1920×1080 (+146),
        // 2560×1440 (+506). Спрятана: 1399×765, 1699×876, 1920×939.
        '@media (max-height: 765.98px)': { display: 'none' },
        '@media (min-width: 1400px) and (max-height: 847.98px)': { display: 'none' },
        '@media (min-width: 1600px) and (max-height: 876.98px)': { display: 'none' },
        '@media (min-width: 1700px) and (max-height: 939.98px)': { display: 'none' },
        // Размеры подобраны замером, а не на глаз: чем ниже табличка, тем на большем числе
        // экранов она помещается по центру, не доставая до карточки запуска сервера внизу справа.
        // Каждые лишние 20px высоты отрезают целый класс окон — сейчас высота 236px.
        width: 172,
        px: 1.75,
        py: 0.75,
        background: 'rgba(10, 10, 10, 0.72)',
        backdropFilter: 'blur(16px)',
        border: `1px solid ${BORDER_COLORS.LIGHT}`,
        borderRadius: '14px',
        // Красный подсвет в тени — тот же приём, что у карточек лендинга на наведении. Здесь он
        // постоянный и очень слабый: табличка стоит на тёмном арте, и без него её край на нём
        // теряется.
        boxShadow: '0 16px 40px rgba(0,0,0,0.55), 0 0 24px rgba(185, 28, 28, 0.1)',
        overflow: 'hidden',
        // Красная полоса по верхней грани вместо строки-заголовка: заголовок стоил бы ещё
        // 30px высоты, которых нет, а отличить табличку от случайного тёмного прямоугольника
        // на арте надо.
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: `linear-gradient(90deg, transparent, ${ACCENT_COLORS.PRIMARY_LIGHT}, transparent)`,
        },
      }}
    >
      {STATS.map((item, i) => (
        <Box
          key={item.id}
          sx={{
            ...ROW_SX,
            // Разделители между строками, но не над первой и не под последней — иначе
            // у рамки появляется вторая линия впритык.
            borderTop: i === 0 ? 0 : `1px solid ${BORDER_COLORS.LIGHT}`,
          }}
        >
          <Typography sx={LABEL_SX}>{t(item.labelKey)}</Typography>
          <Typography
            sx={{ ...VALUE_SX, color: item.accent ? ACCENT_COLORS.PRIMARY_LIGHT : '#fff' }}
          >
            {item.value}
          </Typography>
        </Box>
      ))}

      <Box sx={{ ...ROW_SX, borderTop: `1px solid ${BORDER_COLORS.LIGHT}` }}>
        <Typography sx={LABEL_SX}>{t('stats.status')}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              flex: 'none',
              background: STATUS_COLOR,
              boxShadow: `0 0 8px ${STATUS_COLOR}`,
            }}
          />
          <Typography sx={{ ...VALUE_SX, color: STATUS_COLOR }}>
            {t(SERVER_ONLINE ? 'stats.online' : 'stats.offline')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ServerStatsRail;
